import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useVendor } from '../../contexts/VendorContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Store, Image as ImageIcon, Save, Upload, CheckCircle2, MapPin, Navigation, ArrowLeft, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import LoadingSpinner from '../../components/LoadingSpinner';
import axios from 'axios';

export default function VendorStoreSettings() {
  const { token } = useVendor();
  const [profile, setProfile] = useState({
    business_name: '',
    business_description: '',
    logo_url: '',
    // Pickup location fields
    pickup_address: '',
    pickup_city: '',
    pickup_state: '',
    pickup_pincode: '',
    pickup_latitude: null,
    pickup_longitude: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const logoInputRef = useRef(null);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (token) {
      fetchProfile();
    }
  }, [token]);

  // Initialize map when profile is loaded
  useEffect(() => {
    if (!loading && window.google && window.google.maps && mapRef.current && !mapInstanceRef.current) {
      initializeMap();
    }
    if (!loading && window.google && window.google.maps && addressInputRef.current && !autocompleteRef.current) {
      initializeAutocomplete();
    }
  }, [loading]);
  // Note: initializeMap and initializeAutocomplete are stable functions

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/vendor/profile/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setProfile({
          business_name: response.data.business_name || '',
          business_description: response.data.business_description || response.data.description || '',
          logo_url: response.data.logo_url || response.data.logo || '',
          pickup_address: response.data.pickup_address || response.data.address?.street || '',
          pickup_city: response.data.pickup_city || response.data.address?.city || '',
          pickup_state: response.data.pickup_state || response.data.address?.state || '',
          pickup_pincode: response.data.pickup_pincode || response.data.address?.pincode || '',
          pickup_latitude: response.data.pickup_latitude || null,
          pickup_longitude: response.data.pickup_longitude || null
        });
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load store settings');
    } finally {
      setLoading(false);
    }
  };

  // Handle logo upload
  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/api/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setProfile(prev => ({ ...prev, logo_url: response.data.url }));
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Logo upload failed:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const initializeMap = () => {
    if (!mapRef.current) return;
    
    // Clean up existing map instance
    if (mapInstanceRef.current) {
      return; // Map already initialized
    }
    
    // Default to Chennai if no coordinates
    const defaultLat = profile.pickup_latitude || 13.0827;
    const defaultLng = profile.pickup_longitude || 80.2707;
    
    try {
      const mapOptions = {
        center: { lat: defaultLat, lng: defaultLng },
        zoom: profile.pickup_latitude ? 15 : 10,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true
      };
      
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, mapOptions);
      
      // Add marker
      markerRef.current = new window.google.maps.Marker({
        position: { lat: defaultLat, lng: defaultLng },
        map: mapInstanceRef.current,
        draggable: true,
        title: 'Pickup Location'
      });
      
      // Update coordinates when marker is dragged
      markerRef.current.addListener('dragend', () => {
        const position = markerRef.current.getPosition();
        setProfile(prev => ({
          ...prev,
          pickup_latitude: position.lat(),
          pickup_longitude: position.lng()
        }));
        
        // Reverse geocode to get address
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: position }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const place = results[0];
            updateAddressFromPlace(place);
          }
        });
      });
      
      // Click on map to move marker
      mapInstanceRef.current.addListener('click', (e) => {
        markerRef.current.setPosition(e.latLng);
        setProfile(prev => ({
          ...prev,
          pickup_latitude: e.latLng.lat(),
          pickup_longitude: e.latLng.lng()
        }));
      });
      
      setMapLoaded(true);
    } catch (error) {
      console.error('Failed to initialize map:', error);
    }
  };
  
  const initializeAutocomplete = () => {
    if (!addressInputRef.current || autocompleteRef.current) return;
    
    autocompleteRef.current = new window.google.maps.places.Autocomplete(addressInputRef.current, {
      types: ['geocode', 'establishment'],
      componentRestrictions: { country: 'in' },
      fields: ['address_components', 'geometry', 'formatted_address', 'name']
    });
    
    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      if (!place.geometry) return;
      
      updateAddressFromPlace(place);
      
      // Update map
      if (mapInstanceRef.current && markerRef.current) {
        mapInstanceRef.current.setCenter(place.geometry.location);
        mapInstanceRef.current.setZoom(16);
        markerRef.current.setPosition(place.geometry.location);
      }
      
      setProfile(prev => ({
        ...prev,
        pickup_latitude: place.geometry.location.lat(),
        pickup_longitude: place.geometry.location.lng()
      }));
    });
  };
  
  const updateAddressFromPlace = (place) => {
    let street = '';
    let city = '';
    let state = '';
    let pincode = '';
    
    place.address_components?.forEach(component => {
      const types = component.types;
      if (types.includes('street_number')) street = component.long_name + ' ' + street;
      if (types.includes('route')) street += component.long_name;
      if (types.includes('sublocality_level_1') || types.includes('locality')) {
        city = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) state = component.long_name;
      if (types.includes('postal_code')) pincode = component.long_name;
    });
    
    const fullAddress = place.formatted_address || street.trim();
    
    setProfile(prev => ({
      ...prev,
      pickup_address: fullAddress,
      pickup_city: city || prev.pickup_city,
      pickup_state: state || prev.pickup_state,
      pickup_pincode: pincode || prev.pickup_pincode
    }));
  };
  
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    
    toast.loading('Getting your location...');
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        
        setProfile(prev => ({
          ...prev,
          pickup_latitude: latitude,
          pickup_longitude: longitude
        }));
        
        // Update map
        if (mapInstanceRef.current && markerRef.current) {
          const pos = { lat: latitude, lng: longitude };
          mapInstanceRef.current.setCenter(pos);
          mapInstanceRef.current.setZoom(16);
          markerRef.current.setPosition(pos);
        }
        
        // Reverse geocode
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
          if (status === 'OK' && results[0]) {
            updateAddressFromPlace(results[0]);
            toast.dismiss();
            toast.success('Location found!');
          }
        });
      },
      (error) => {
        toast.dismiss();
        toast.error('Failed to get your location. Please allow location access.');
        console.error('Geolocation error:', error);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSave = async () => {
    if (!profile.business_name) {
      toast.error('Business name is required');
      return;
    }
    
    try {
      setSaving(true);
      
      const updateData = {
        business_name: profile.business_name,
        description: profile.business_description,
        logo: profile.logo_url,
        pickup_address: profile.pickup_address,
        pickup_city: profile.pickup_city,
        pickup_state: profile.pickup_state,
        pickup_pincode: profile.pickup_pincode,
        pickup_latitude: profile.pickup_latitude,
        pickup_longitude: profile.pickup_longitude
      };
      
      await axios.put(
        `${API_URL}/api/vendor/profile/settings`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Store settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6 p-6 bg-gray-50 min-h-screen">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <Link to="/vendor/dashboard">
          <Button variant="ghost" size="icon" className="h-10 w-10">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Store Settings</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your store profile, logo, and pickup location</p>
        </div>
      </div>

      {/* Store Profile */}
      <Card className="bg-white shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Store className="w-5 h-5 text-green-600" />
            Store Profile
          </CardTitle>
          <CardDescription className="text-xs text-gray-500">Basic information about your store</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="business_name" className="text-sm font-semibold text-gray-700">Business Name</Label>
            <Input
              id="business_name"
              value={profile.business_name}
              onChange={(e) => setProfile({...profile, business_name: e.target.value})}
              placeholder="Enter your store name"
              className="bg-white text-sm"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="business_description" className="text-sm font-semibold text-gray-700">Business Description</Label>
            <textarea
              id="business_description"
              value={profile.business_description}
              onChange={(e) => setProfile({...profile, business_description: e.target.value})}
              placeholder="Tell customers about your store..."
              className="w-full p-3 border border-gray-300 rounded-lg min-h-[100px] bg-white text-sm placeholder:text-gray-400"
            />
          </div>
        </CardContent>
      </Card>

      {/* Store Logo - with Upload */}
      <Card className="bg-white shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ImageIcon className="w-5 h-5 text-blue-600" />
            Store Logo
          </CardTitle>
          <CardDescription className="text-xs text-gray-500">Your store's brand logo (displayed on vendor page) - Recommended: 200x200 pixels</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-6">
            <div className="w-32 h-32 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 overflow-hidden">
              {profile.logo_url ? (
                <img 
                  src={profile.logo_url.startsWith('http') ? profile.logo_url : `${API_URL}${profile.logo_url}`}
                  alt="Store Logo"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="text-center text-gray-400">
                  <Upload className="w-8 h-8 mx-auto mb-1" />
                  <span className="text-xs">No Logo</span>
                </div>
              )}
            </div>
            
            <div className="flex-1 space-y-3">
              {/* Hidden file input */}
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
              
              {/* Upload button */}
              <Button
                type="button"
                variant="outline"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
                className="w-full"
              >
                {uploadingLogo ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Logo Image
                  </>
                )}
              </Button>
              
              {/* Or enter URL */}
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-gray-700">Or enter image URL</Label>
                <Input
                  value={profile.logo_url}
                  onChange={(e) => setProfile({...profile, logo_url: e.target.value})}
                  placeholder="https://example.com/logo.png"
                  className="bg-white text-sm placeholder:text-gray-400"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pickup Location - Google Maps */}
      <Card className="bg-white shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <MapPin className="w-5 h-5 text-red-600" />
            Pickup Location
          </CardTitle>
          <CardDescription className="text-xs text-gray-500">
            Set your store's pickup location for shipping calculations. Drag the marker or search for your address.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search Address */}
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                ref={addressInputRef}
                placeholder="Search for your pickup location..."
                className="bg-white text-sm placeholder:text-gray-400"
                data-testid="pickup-address-search"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={getCurrentLocation}
              className="flex items-center gap-2"
              data-testid="use-current-location-btn"
            >
              <Navigation className="w-4 h-4" />
              Use My Location
            </Button>
          </div>
          
          {/* Loading indicator (outside the map div) */}
          {!mapLoaded && (
            <div className="w-full h-[300px] rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <MapPin className="w-8 h-8 mx-auto mb-2 animate-pulse" />
                <p className="text-sm">Loading map...</p>
              </div>
            </div>
          )}
          
          {/* Map Container - must have no children for Google Maps */}
          <div 
            ref={mapRef}
            className={`w-full h-[300px] rounded-lg border border-gray-200 bg-gray-100 ${!mapLoaded ? 'hidden' : ''}`}
            data-testid="pickup-location-map"
          />
          
          {/* Address Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 space-y-1.5">
              <Label htmlFor="pickup_address" className="text-sm font-semibold text-gray-700">Full Address</Label>
              <Input
                id="pickup_address"
                value={profile.pickup_address}
                onChange={(e) => setProfile({...profile, pickup_address: e.target.value})}
                placeholder="Street address, building name, etc."
                className="bg-white text-sm placeholder:text-gray-400"
              />
            </div>
            
            <div className="space-y-1.5">
              <Label htmlFor="pickup_city" className="text-sm font-semibold text-gray-700">City</Label>
              <Input
                id="pickup_city"
                value={profile.pickup_city}
                onChange={(e) => setProfile({...profile, pickup_city: e.target.value})}
                placeholder="City name"
                className="bg-white text-sm placeholder:text-gray-400"
              />
            </div>
            
            <div className="space-y-1.5">
              <Label htmlFor="pickup_state" className="text-sm font-semibold text-gray-700">State</Label>
              <Input
                id="pickup_state"
                value={profile.pickup_state}
                onChange={(e) => setProfile({...profile, pickup_state: e.target.value})}
                placeholder="State name"
                className="bg-white text-sm placeholder:text-gray-400"
              />
            </div>
            
            <div className="space-y-1.5">
              <Label htmlFor="pickup_pincode" className="text-sm font-semibold text-gray-700">PIN Code</Label>
              <Input
                id="pickup_pincode"
                value={profile.pickup_pincode}
                onChange={(e) => setProfile({...profile, pickup_pincode: e.target.value})}
                placeholder="Enter PIN code"
                className="bg-white text-sm placeholder:text-gray-400"
              />
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-gray-700">Coordinates</Label>
              <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                {profile.pickup_latitude && profile.pickup_longitude ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>{profile.pickup_latitude.toFixed(6)}, {profile.pickup_longitude.toFixed(6)}</span>
                  </>
                ) : (
                  <span className="text-gray-400">No location set - Search or use current location</span>
                )}
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-700">
              <strong>Tip:</strong> Accurate pickup location helps calculate shipping costs correctly. 
              You can drag the marker on the map to fine-tune your location.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <Link to="/vendor/dashboard">
          <Button variant="outline" size="lg">
            Cancel
          </Button>
        </Link>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-green-600 hover:bg-green-700"
          size="lg"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Store Settings
        </Button>
      </div>
    </div>
  );
}
